/**
 * Suggest an Improvement page — same URL (/suggest) for staff and installers.
 *
 * Two panels:
 *  1) A short form: Title, Details, and (optionally) an Area chip.
 *  2) "My suggestions so far" list with the current status of each idea.
 *
 * Admin/manager users additionally see a "Go to Suggestions Board" button
 * at the top so they can jump straight to the review Kanban.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Home, Lightbulb, Send, Loader2, CheckCircle2, Clock, Sparkles, XCircle, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import useHomePath from '@/hooks/useHomePath';
import { formatApiError } from '@/utils/apiError';

const STATUS_META = {
  new:           { label: 'New',           chip: 'bg-slate-100 text-slate-800',   Icon: Sparkles },
  under_review:  { label: 'Under Review',  chip: 'bg-blue-100 text-blue-800',     Icon: Clock },
  planned:       { label: 'Planned',       chip: 'bg-violet-100 text-violet-800', Icon: Layers },
  completed:     { label: 'Completed',     chip: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
  declined:      { label: 'Declined',      chip: 'bg-rose-100 text-rose-700',     Icon: XCircle },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export default function SuggestBox() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const homePath = useHomePath();
  const isAdmin = ['admin', 'manager'].includes(user?.role);

  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [saving, setSaving] = useState(false);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMine = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/suggestions/mine`, { credentials: 'include' });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      setMine(await r.json());
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) loadMine(); }, [user, loadMine]);

  const submit = async () => {
    if (!title.trim() || !details.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/suggestions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), details: details.trim() }),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed to send');
      toast.success('Thanks — your suggestion is in!');
      setTitle(''); setDetails('');
      loadMine();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #7c2d12, #b45309)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate(homePath)} data-testid="suggest-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Home">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <Lightbulb className="w-5 h-5 shrink-0" /> Suggest an Improvement
            </h1>
            <p className="hidden sm:block text-white/80 text-xs">Spot something clunky? Got an idea? Fire away — we read every one.</p>
          </div>
          {isAdmin && (
            <Button
              onClick={() => navigate('/staff/suggestions')}
              size="sm"
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
              data-testid="suggest-goto-board"
            >
              <Layers className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Suggestions Board</span>
            </Button>
          )}
          <Button onClick={() => navigate(homePath)} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0">
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <Card className="p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">New suggestion</h2>
          <div className="space-y-3">
            <div>
              <Label htmlFor="s-title">Title <span className="text-rose-600">*</span></Label>
              <Input
                id="s-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Add a dark mode"
                className="mt-1"
                data-testid="suggest-title"
                maxLength={200}
              />
            </div>
            <div>
              <Label htmlFor="s-details">What would you like to see? <span className="text-rose-600">*</span></Label>
              <Textarea
                id="s-details"
                rows={5}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Tell us the problem you're trying to solve and, if you can, how you imagine it working."
                className="mt-1"
                data-testid="suggest-details"
                maxLength={4000}
              />
              <div className="text-[11px] text-slate-400 mt-1">{details.length}/4000</div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={submit}
                disabled={saving || !title.trim() || !details.trim()}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="suggest-submit"
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-1" /> Submit suggestion</>}
              </Button>
            </div>
          </div>
        </Card>

        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Your suggestions</h2>
          {loading ? (
            <Card className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></Card>
          ) : mine.length === 0 ? (
            <Card className="p-8 text-center text-sm text-slate-500 italic" data-testid="suggest-mine-empty">
              You haven't sent any suggestions yet — nothing's too small.
            </Card>
          ) : (
            <div className="space-y-2">
              {mine.map((s) => {
                const meta = STATUS_META[s.status] || STATUS_META.new;
                const StatusIcon = meta.Icon;
                return (
                  <Card key={s.id} className="p-4" data-testid={`suggest-mine-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900 truncate">{s.title}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{s.reference}</div>
                      </div>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${meta.chip}`}>
                        <StatusIcon className="w-3 h-3" /> {meta.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap line-clamp-3">{s.details}</p>
                    <div className="text-[11px] text-slate-400 mt-2">Sent {fmt(s.created_at)}</div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
